import { Component, type ErrorInfo, type ReactNode } from 'react';
import { track, classifyError } from '@/analytics';
import { crashReport, recordCrash, type CrashRecord } from '@/core/diagnostics/crashCapture';
import { crashProps, crashSite } from '@/core/diagnostics/crashFrames';
import { Notice } from '@/components/ui/primitives/Notice';

const RELOAD_KEY = 'pmt-chunk-error-reloaded';

function isChunkLoadError(err: unknown): boolean {
  if (!(err instanceof Error)) return false;
  const msg = err.message;
  return (
    err.name === 'ChunkLoadError' ||
    msg.includes('Failed to fetch dynamically imported module') ||
    msg.includes('error loading dynamically imported module') ||
    msg.includes('Importing a module script failed')
  );
}

interface Props {
  children: ReactNode;
  /** Custom fallback rendered instead of the built-in reload UI. */
  fallback?: ReactNode;
}

/** The browser says there is no network (a chunk that is not precached cannot load: docs/offline.md). */
function isOffline(): boolean {
  return typeof navigator !== 'undefined' && navigator.onLine === false;
}

interface State {
  hasError: boolean;
  isChunkError: boolean;
  /** A chunk failed while offline: say so, and never reload (offline, a reload lands on the browser's error page). */
  offline?: boolean;
  crash?: CrashRecord;
  copied?: 'ok' | 'failed';
}

/**
 * The app's render-crash boundary (mounted at the app root and around the lazy views and panels). A lazy-chunk failure reloads the page
 * once (on first occurrence); any other crash -- and a second chunk failure -- is recorded on the device
 * (core/diagnostics/crashCapture.ts), reported as `operation_failed`, and shown as "Couldn't load this view" with
 * the error's name and message, Reload and Copy details.
 */
export class ChunkErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, isChunkError: false };

  static getDerivedStateFromError(err: unknown): State {
    const isChunkError = isChunkLoadError(err);
    return { hasError: true, isChunkError, offline: isChunkError && isOffline() };
  }

  componentDidCatch(err: unknown, info?: ErrorInfo): void {
    const error_kind = classifyError(err);
    // What the crash WAS, kept on the device (sessionStorage + the ?inkdebug log) for "Copy details".
    let crash: CrashRecord | undefined;
    try { crash = recordCrash('boundary', err, info?.componentStack); this.setState({ crash }); } catch { /* never break the fallback */ }
    const error_site = crash ? crashSite(crash.components) : 'unknown';
    // Where it broke, as bundle code locations (never the message) -- core/diagnostics/crashFrames.ts.
    let where: ReturnType<typeof crashProps> | Record<string, never> = {};
    try { where = crashProps(err, info?.componentStack); } catch { /* never break the fallback */ }

    if (isChunkLoadError(err) && isOffline()) {
      // Offline: this part of the app is not precached (cloud, 3D, compare). No reload (it would leave the app for
      // the browser's offline error page); the fallback says why and offers Reload for when the network is back.
      try { track('operation_failed', { operation: 'render-chunk-offline', error_kind, error_site, ...where }); } catch { /* never break the fallback */ }
      return;
    }
    if (isChunkLoadError(err)) {
      const alreadyReloaded = sessionStorage.getItem(RELOAD_KEY) === '1';
      if (!alreadyReloaded) {
        // Emit before reload -- the reload tears down the page.
        try { track('operation_failed', { operation: 'render-chunk-reload', error_kind, error_site, ...where }); } catch { /* must not break the reload */ }
        sessionStorage.setItem(RELOAD_KEY, '1');
        window.location.reload();
        return;
      }
    }
    // Non-chunk error or second chunk failure: clear the flag so the next fresh
    // navigation can still auto-reload if needed.
    try { track('operation_failed', { operation: 'render', error_kind, error_site, ...where }); } catch { /* must not break the fallback UI */ }
    sessionStorage.removeItem(RELOAD_KEY);
  }

  private handleCopy = async (): Promise<void> => {
    const text = crashReport();
    try {
      await navigator.clipboard.writeText(text);
      this.setState({ copied: 'ok' });
    } catch {
      this.setState({ copied: 'failed' });
    }
  };

  private handleReload = (): void => {
    sessionStorage.removeItem(RELOAD_KEY);
    window.location.reload();
  };

  render(): ReactNode {
    if (!this.state.hasError) return this.props.children;
    if (this.props.fallback) return this.props.fallback;
    if (this.state.offline) {
      return (
        <Notice tone="info" role="status" testId="offline-notice" extra={(
          <button type="button" onClick={this.handleReload}>
            Reload
          </button>
        )}>
          <p>You&apos;re offline. This part of the app needs a connection.</p>
        </Notice>
      );
    }
    return (
      <Notice tone="alert" role="alert" extra={(
        <>
          <button type="button" onClick={this.handleReload}>
            Reload
          </button>
          <button type="button" onClick={this.handleCopy} data-testid="crash-copy">
            {this.state.copied === 'ok' ? 'Copied' : 'Copy details'}
          </button>
        </>
      )}>
        <p>Couldn&apos;t load this view.</p>
        {this.state.crash && !this.state.isChunkError && (
          <p className="chunk-error-fallback__detail" data-testid="crash-detail">
            {this.state.crash.name}: {this.state.crash.message}
          </p>
        )}
        {this.state.copied === 'failed' && (
          <textarea className="chunk-error-fallback__text" readOnly value={crashReport()} aria-label="Crash details" />
        )}
      </Notice>
    );
  }
}
